import { memo } from 'react';
import { Button, TextButton } from '@zillow/constellation-10';
import type { YourRentalCtaDto } from '@zg-rentals/ts-your-rental-api-client';
import { CtaType } from '@zg-rentals/ts-your-rental-api-client';
import { useAddCurrentQueryParams, useIsInMobileWebView, useYourRentalCtaHandler } from '../../hooks';

type CtaButtonProps = {
  cta: YourRentalCtaDto;
  listingAlias?: string;
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  dataTestId?: string;
};

const CtaButton = memo(({ cta, listingAlias, size = 'sm', fluid = false, dataTestId }: CtaButtonProps) => {
  const isInMobileWebView = useIsInMobileWebView();
  const handleCta = useYourRentalCtaHandler({ listingAlias, isInMobileWebView });
  const addQueryParams = useAddCurrentQueryParams();

  const handleClick = (event: React.MouseEvent) => {
    const wasHandledNatively = handleCta(cta);
    if (wasHandledNatively) {
      event.preventDefault();
    }
  };

  const content = cta.url ? (
    <a
      href={addQueryParams(cta.url)}
      aria-label={cta.text}
      data-testid={dataTestId}
      {...(!isInMobileWebView && { target: '_blank', rel: 'noreferrer' })}
    >
      {cta.text}
    </a>
  ) : (
    cta.text
  );

  if (cta.type === CtaType.TEXT_BUTTON) {
    return (
      <TextButton asChild={Boolean(cta.url)} onClick={handleClick}>
        {content}
      </TextButton>
    );
  }

  return (
    <Button
      asChild={Boolean(cta.url)}
      onClick={handleClick}
      size={size}
      fluid={fluid}
      emphasis={CtaType.PRIMARY_BUTTON === cta.type ? 'filled' : 'outlined'}
      aria-label={cta.text}
      data-testid={dataTestId}
    >
      {content}
    </Button>
  );
});

CtaButton.displayName = 'CtaButton';

export default CtaButton;
